import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Trash2, RotateCcw, Download, Upload, Loader2, AlertTriangle,
  BookOpen, Layers, FileText, Clock, History, XCircle, CheckCircle2, Search,
} from 'lucide-react';

const TABS = [
  { id: 'trash', label: 'Trash', icon: Trash2 },
  { id: 'export', label: 'Export / Import', icon: Download },
  { id: 'versions', label: 'Version History', icon: History },
];

const TYPE_META = {
  course: { icon: BookOpen, color: 'text-blue-600 bg-blue-50', label: 'Course' },
  module: { icon: Layers, color: 'text-purple-600 bg-purple-50', label: 'Module' },
  element: { icon: FileText, color: 'text-teal-600 bg-teal-50', label: 'Element' },
};

const AdminDataPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('trash');
  const [loading, setLoading] = useState(false);

  // Trash
  const [trash, setTrash] = useState({ courses: [], modules: [], elements: [], counts: { total: 0 } });
  const [confirmPurge, setConfirmPurge] = useState(null);

  // Export/Import
  const [exportData, setExportData] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);

  // Versions
  const [courseSearch, setCourseSearch] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [versions, setVersions] = useState([]);
  const [confirmRestore, setConfirmRestore] = useState(null);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/trash');
      setTrash(res.data);
    } catch { toast.error('Failed to load trash'); }
    finally { setLoading(false); }
  }, [api]);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/builder/courses?limit=100');
      setCourses(res.data || []);
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => {
    if (activeTab === 'trash') fetchTrash();
    if (activeTab === 'versions') fetchCourses();
  }, [activeTab, fetchTrash, fetchCourses]);

  // Trash actions
  const handleRestore = async (type, id) => {
    try {
      await api.post(`/admin/data/trash/restore/${type}/${id}`);
      toast.success('Restored successfully');
      fetchTrash();
    } catch { toast.error('Restore failed'); }
  };

  const handlePurge = async (type, id) => {
    try {
      await api.delete(`/admin/data/trash/purge/${type}/${id}`);
      toast.success('Permanently deleted');
      setConfirmPurge(null);
      fetchTrash();
    } catch { toast.error('Delete failed'); }
  };

  const handlePurgeExpired = async () => {
    try {
      const res = await api.post('/admin/data/trash/purge-expired');
      toast.success(`Purged: ${res.data.deleted.courses} courses, ${res.data.deleted.modules} modules, ${res.data.deleted.elements} elements`);
      fetchTrash();
    } catch { toast.error('Purge failed'); }
  };

  // Export
  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/export');
      setExportData(res.data);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `common_impact_courses_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success(`Exported ${res.data.counts.courses} courses, ${res.data.counts.modules} modules, ${res.data.counts.elements} elements`);
    } catch { toast.error('Export failed'); }
    finally { setLoading(false); }
  };

  // Import
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setImportData(data);
      const res = await api.post('/admin/data/import/preview', data);
      setImportPreview(res.data);
    } catch { toast.error('Invalid JSON file'); }
  };

  const handleImport = async () => {
    if (!importData) return;
    setImporting(true);
    try {
      const res = await api.post('/admin/data/import', importData);
      toast.success(`Imported: ${res.data.results.courses_new} new, ${res.data.results.courses_updated} updated`);
      setImportPreview(null);
      setImportData(null);
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  // Versions
  const fetchVersions = async (courseId) => {
    setSelectedCourseId(courseId);
    try {
      const res = await api.get(`/admin/data/versions/${courseId}`);
      setVersions(res.data.versions || []);
    } catch { toast.error('Failed to load versions'); }
  };

  const handleSaveVersion = async (courseId) => {
    try {
      const res = await api.post(`/admin/data/versions/${courseId}/save`);
      toast.success(res.data.message);
      fetchVersions(courseId);
    } catch { toast.error('Failed to save version'); }
  };

  const handleRestoreVersion = async (courseId, versionId) => {
    try {
      const res = await api.post(`/admin/data/versions/${courseId}/restore/${versionId}`);
      toast.success(res.data.message);
      setConfirmRestore(null);
      fetchVersions(courseId);
    } catch { toast.error('Restore failed'); }
  };

  const daysUntilPurge = (deletedAt) => {
    if (!deletedAt) return 30;
    const d = new Date(deletedAt);
    const now = new Date();
    return Math.max(0, 30 - Math.floor((now - d) / (1000 * 60 * 60 * 24)));
  };

  const filteredCourses = courses.filter(c =>
    !courseSearch || c.title?.toLowerCase().includes(courseSearch.toLowerCase())
  );

  const renderTrashItem = (item, type) => {
    const meta = TYPE_META[type];
    const Icon = meta.icon;
    const days = daysUntilPurge(item.deleted_at);
    return (
      <div key={item.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors" data-testid={`trash-item-${item.id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{item.title || item.name || 'Untitled'}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0">{meta.label}</Badge>
              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                <Clock className="h-3 w-3" />{days} days left
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleRestore(type, item.id)} data-testid={`restore-${item.id}`}>
            <RotateCcw className="h-3 w-3" /> Restore
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmPurge({ type, id: item.id, title: item.title || 'this item' })} data-testid={`purge-${item.id}`}>
            <XCircle className="h-3 w-3" /> Delete
          </Button>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-5" data-testid="admin-data-page">
        <div>
          <h1 className="text-2xl font-bold text-[#2a3e5d]">Data Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Trash, export/import, and version history</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white text-[#2a3e5d] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.id === 'trash' && trash.counts.total > 0 && (
                  <span className="text-[10px] bg-red-100 text-red-600 rounded-full px-1.5 min-w-[18px] text-center">{trash.counts.total}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* TRASH TAB */}
        {activeTab === 'trash' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">{trash.counts.total} items in trash</p>
              {trash.counts.total > 0 && (
                <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={handlePurgeExpired} data-testid="purge-expired-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Purge expired (&gt;30 days)
                </Button>
              )}
            </div>
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : trash.counts.total === 0 ? (
              <Card className="border-slate-200"><CardContent className="py-16 text-center text-slate-400">
                <Trash2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Trash is empty</p>
                <p className="text-xs mt-1">Deleted courses, modules, and elements will appear here for 30 days</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {trash.courses.map(c => renderTrashItem(c, 'course'))}
                {trash.modules.map(m => renderTrashItem(m, 'module'))}
                {trash.elements.map(e => renderTrashItem(e, 'element'))}
              </div>
            )}
          </div>
        )}

        {/* EXPORT/IMPORT TAB */}
        {activeTab === 'export' && (
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#20acf6]/10 rounded-xl flex items-center justify-center">
                    <Download className="h-5 w-5 text-[#20acf6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2a3e5d]">Export Course Data</h3>
                    <p className="text-xs text-slate-500">Download all courses, modules, and elements as JSON</p>
                  </div>
                </div>
                <Button className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleExport} disabled={loading} data-testid="export-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Export All Course Data
                </Button>
                {exportData && (
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> {exportData.counts.courses} courses, {exportData.counts.modules} modules, {exportData.counts.elements} elements
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Upload className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2a3e5d]">Import Course Data</h3>
                    <p className="text-xs text-slate-500">Restore courses from a JSON backup file</p>
                  </div>
                </div>
                <Input type="file" accept=".json" onChange={handleFileUpload} className="mb-3" data-testid="import-file-input" />
                {importPreview && (
                  <div className="p-3 bg-slate-50 rounded-lg mb-3 space-y-1">
                    <p className="text-xs font-semibold text-slate-700">Import Preview:</p>
                    <p className="text-xs text-slate-600">{importPreview.preview.new_courses} new courses</p>
                    <p className="text-xs text-slate-600">{importPreview.preview.updated_courses} existing courses (will update)</p>
                    <p className="text-xs text-slate-600">{importPreview.preview.total_modules} modules, {importPreview.preview.total_elements} elements</p>
                    {importPreview.new_course_titles.length > 0 && (
                      <div className="mt-2"><p className="text-[10px] text-slate-500">New: {importPreview.new_course_titles.join(', ')}</p></div>
                    )}
                  </div>
                )}
                <Button className="w-full" variant="outline" onClick={handleImport} disabled={!importData || importing} data-testid="import-btn">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Import Data
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* VERSION HISTORY TAB */}
        {activeTab === 'versions' && (
          <div className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search courses..." value={courseSearch} onChange={e => setCourseSearch(e.target.value)} className="pl-10 h-9" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Course list */}
              <Card className="border-slate-200">
                <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                  {filteredCourses.length === 0 ? (
                    <p className="text-center text-slate-400 py-8 text-sm">No courses found</p>
                  ) : filteredCourses.map(c => (
                    <button key={c.id} onClick={() => fetchVersions(c.id)}
                      className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedCourseId === c.id ? 'bg-[#20acf6]/5 border-l-2 border-l-[#20acf6]' : ''}`}
                      data-testid={`version-course-${c.id}`}
                    >
                      <p className="text-sm font-medium text-slate-800 truncate">{c.title}</p>
                      <p className="text-[10px] text-slate-400">{c.modules?.length || 0} modules</p>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Version list */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  {!selectedCourseId ? (
                    <p className="text-center text-slate-400 py-8 text-sm">Select a course to view versions</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-[#2a3e5d]">Versions ({versions.length})</h3>
                        <Button size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7] h-7 text-xs" onClick={() => handleSaveVersion(selectedCourseId)} data-testid="save-version-btn">
                          Save Current Version
                        </Button>
                      </div>
                      {versions.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">No versions saved yet</p>
                      ) : (
                        <div className="space-y-2">
                          {versions.map(v => (
                            <div key={v.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between" data-testid={`version-${v.id}`}>
                              <div>
                                <p className="text-sm font-medium text-slate-700">Version {v.version_number}</p>
                                <p className="text-[10px] text-slate-400">
                                  {v.saved_at ? new Date(v.saved_at).toLocaleString() : ''} &middot; {v.saved_by_name}
                                </p>
                              </div>
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setConfirmRestore(v)} data-testid={`restore-version-${v.id}`}>
                                <RotateCcw className="h-3 w-3" /> Restore
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Purge Dialog */}
      <Dialog open={!!confirmPurge} onOpenChange={() => setConfirmPurge(null)}>
        <DialogContent data-testid="confirm-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Permanently Delete?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will permanently delete <strong>{confirmPurge?.title}</strong>. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPurge(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handlePurge(confirmPurge.type, confirmPurge.id)} data-testid="confirm-purge-btn">
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Version Restore Dialog */}
      <Dialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <DialogContent data-testid="confirm-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-[#20acf6]" /> Restore Version {confirmRestore?.version_number}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will restore the course to version {confirmRestore?.version_number}. The current state will be saved as a backup version first.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRestore(null)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={() => handleRestoreVersion(selectedCourseId, confirmRestore.id)} data-testid="confirm-restore-btn">
              Restore Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminDataPage;
